/**
 * Zod DTOs for the auth + setup API surface (frozen contract, spec §13/§14/§15).
 * Shared so the web client validates identically.
 */
import { z } from "zod";
import { SUPPORTED_LOCALES } from "../locales.js";
import { employeeInvitationProfileSchema } from "./employees.js";
/** Password policy: min 10 chars, at least one letter and one number. */
export const passwordPolicySchema = z
  .string()
  .min(10, "Password must be at least 10 characters")
  .regex(/[A-Za-z]/, "Password must contain a letter")
  .regex(/[0-9]/, "Password must contain a number");

const localeSchema = z.enum(SUPPORTED_LOCALES);

export const userLocaleDtoSchema = z.object({
  locale: localeSchema,
});
export type UserLocaleDto = z.infer<typeof userLocaleDtoSchema>;

export const loginDtoSchema = z
  .object({
    identifier: z.string().trim().min(1).max(320).optional(),
    /** Temporary compatibility alias while clients migrate to identifier. */
    email: z.string().trim().toLowerCase().email().optional(),
    password: z.string().min(1),
    rememberMe: z.boolean().optional().default(false),
  })
  .refine((value) => Boolean(value.identifier ?? value.email), {
    path: ["identifier"],
    message: "Login identifier is required",
  })
  .transform((value) => ({
    ...value,
    identifier: value.identifier ?? value.email!,
  }));
export type LoginDto = z.infer<typeof loginDtoSchema>;

export const forgotPasswordDtoSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
});
export type ForgotPasswordDto = z.infer<typeof forgotPasswordDtoSchema>;

export const resetPasswordDtoSchema = z.object({
  token: z.string().min(1),
  password: passwordPolicySchema,
});
export type ResetPasswordDto = z.infer<typeof resetPasswordDtoSchema>;
export const changePasswordDtoSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: passwordPolicySchema,
  })
  .refine((value) => value.currentPassword !== value.newPassword, {
    path: ["newPassword"],
    message: "New password must differ from the current password",
  });
export type ChangePasswordDto = z.infer<typeof changePasswordDtoSchema>;
export const emailChangeRequestDtoSchema = z
  .object({
    currentPassword: z.string().min(1),
    newEmail: z.string().trim().toLowerCase().email().max(320),
  })
  .strict();
export type EmailChangeRequestDto = z.infer<typeof emailChangeRequestDtoSchema>;
export const emailChangeRequestSchema = emailChangeRequestDtoSchema;

export const emailChangeConfirmationDtoSchema = z
  .object({
    token: z.string().trim().min(1).max(512),
  })
  .strict();
export type EmailChangeConfirmationDto = z.infer<
  typeof emailChangeConfirmationDtoSchema
>;
export const emailChangeConfirmSchema = emailChangeConfirmationDtoSchema;

/** Management-initiated changes still require confirmation of the new address. */
export const userEmailChangeSchema = z
  .object({
    newEmail: z.string().trim().toLowerCase().email().max(320),
    version: z.number().int().positive(),
  })
  .strict();
export type UserEmailChangeDto = z.infer<typeof userEmailChangeSchema>;

export const setupValidateTokenDtoSchema = z.object({
  token: z.string().min(1),
});
export type SetupValidateTokenDto = z.infer<typeof setupValidateTokenDtoSchema>;

const taxRateDtoSchema = z.object({
  name: z.string().trim().min(1).max(80),
  rate: z
    .union([
      z.number().finite().min(0).max(100),
      z.string().regex(/^\d{1,3}(\.\d{1,3})?$/),
    ])
    .transform((v) => String(v)),
  isDefault: z.boolean().optional().default(false),
});

export const setupCompleteDtoSchema = z.object({
  credential: z.union([
    z.object({ setupToken: z.string().min(1) }),
    z.object({ invitationToken: z.string().min(1) }),
  ]),
  locale: localeSchema,
  company: z.object({
    name: z.string().trim().min(1).max(200),
    tradingName: z.string().trim().max(200).optional(),
    addressLine1: z.string().trim().min(1).max(200),
    addressLine2: z.string().trim().max(200).optional(),
    postalCode: z.string().trim().min(1).max(20),
    city: z.string().trim().min(1).max(120),
    canton: z.string().trim().max(60).optional(),
    country: z.string().trim().length(2).default("CH"),
    email: z.string().trim().toLowerCase().email(),
    phone: z.string().trim().max(60).optional(),
    website: z.string().trim().url().max(300).optional(),
    vatNumber: z.string().trim().max(40).optional(),
    industry: z
      .enum([
        "landscaping",
        "general_services",
        "construction",
        "cleaning",
        "other",
      ])
      .default("landscaping"),
  }),
  systemAdmin: z.object({
    password: passwordPolicySchema,
  }),
  localization: z.object({
    defaultLocale: localeSchema,
    timezone: z.string().trim().min(1).max(80),
    currency: z.string().trim().length(3).default("CHF"),
  }),
  billing: z.object({
    vatRegistered: z.boolean(),
    paymentTermDays: z.number().int().min(0).max(365),
    iban: z.string().trim().max(40).optional(),
    bankName: z.string().trim().max(120).optional(),
    referenceType: z.enum(["NON", "SCOR", "QRR"]).default("NON"),
    taxRates: z.array(taxRateDtoSchema).min(1),
  }),
  documents: z.object({
    offerValidityDays: z.number().int().min(1).max(365),
  }),
});
export type SetupCompleteDto = z.infer<typeof setupCompleteDtoSchema>;

export const invitationAcceptDtoSchema = z.object({
  password: passwordPolicySchema,
  locale: localeSchema.optional(),
  acceptTerms: z.literal(true),
  /** Employee profile collected during invitation acceptance. */
  profile: employeeInvitationProfileSchema.optional(),
});
export type InvitationAcceptDto = z.infer<typeof invitationAcceptDtoSchema>;
